import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useQueryClient } from "@tanstack/react-query";
import TemplateBasedColorIcon from "../../assets/interaction-detail-page/template-based-color-icon.svg";
import UserInteractionsService from "../../services/interactions/Interactions";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import {
  TemplateItem,
  PostTemplateCreateOrUpdateParams,
  EditTemplateBasedExtractionProps,
} from "../../interfaces/templateBasedExtraction";
import "../../styles/templateBasedExtraction.css";
import CustomConfirmation from "../Common/CustomConfirmation";
import EditTemplates from "./EditTemplates";

const getTemplateIdFromResponse = (response: any): string | undefined => {
  const payloads = [response, response?.data, response?.data?.data];
  for (const payload of payloads) {
    const templateId = payload?.templateId || payload?.id;
    if (templateId) return String(templateId);
  }
  return undefined;
};

export const EditTemplateBasedExtraction: React.FC<
  EditTemplateBasedExtractionProps
> = ({
  template,
  templates = [],
  isCreateMode = false,
  isRegenerateMode = false,
  isSaving = false,
  interactionId,
  transcriptAssetId,
  collaterals,
  onCancel,
  onSave,
  onSaveSuccess,
  onRun,
}) => {
  const [templateName, setTemplateName] = useState<string>("");
  const [prompt, setPrompt] = useState<string>("");
  const [originalValues, setOriginalValues] = useState<{
    name: string;
    prompt: string;
  }>({
    name: "",
    prompt: "",
  });
  const [isEditing, setIsEditing] = useState<boolean>(true);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState<boolean>(false);

  const justSavedRef = useRef(false);
  const prevTemplateIdRef = useRef<string | undefined>(template?.id);
  const hasUserEditedPromptRef = useRef(false);
  const hasUserEditedNameRef = useRef(false);
  const pendingOutsideClickRef = useRef<HTMLElement | null>(null);

  const queryClient = useQueryClient();
  const { showSnackbar } = useDisplaySnackbar();
  const { refetch: refetchTemplateList } =
    UserInteractionsService.useGetTemplateList({}, false);

  const refreshTemplateList = async () => {
    await queryClient.invalidateQueries({ queryKey: ["get_template_list"] });
    await refetchTemplateList();
  };

  const {
    mutateAsync: postTemplateCreateOrUpdate,
    isPending: isSavingMutation,
  } = UserInteractionsService.usePostTemplateCreateOrUpdate();

  const {
    mutateAsync: generateTemplateExtractionV2Mutation,
    isPending: isGeneratingExtraction,
  } = UserInteractionsService.usePostGenerateTemplateBasedExtractionV2();

  const effectiveSaving =
    isSaving || isSavingMutation || isGeneratingExtraction;

  const matchingCollateral = React.useMemo(() => {
    if (!Array.isArray(collaterals) || collaterals.length === 0 || !template)
      return null;
    return (
      collaterals.find((col: any) => {
        if (col.assetType !== "template_based_extraction_v2") return false;
        const colAssetId = col.interactionAssetId;
        const colTemplateId = col.assetMetaData?.templateId;
        if (template.assetId) return colAssetId === template.assetId;
        if (template.id && colAssetId === template.id) return true;
        return Boolean(template.templateId && colTemplateId === template.templateId);
      }) || null
    );
  }, [collaterals, template]);

  const rawList = React.useMemo(() => {
    return templates;
  }, [templates]);

  const matchedTemplate = React.useMemo(() => {
    if (!rawList.length || !template) return null;
    const targetIds = new Set(
      [template.templateId, template.id, template.assetId]
        .filter(Boolean)
        .map(String),
    );
    return rawList.find((item: any) => {
      const itemId = String(
        item.templateId || item.template_id || item.id || item._id || "",
      );
      return itemId && targetIds.has(itemId);
    }) || null;
  }, [rawList, template]);

  useEffect(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      prevTemplateIdRef.current = template?.id;
      return;
    }

    if (prevTemplateIdRef.current !== template?.id) {
      prevTemplateIdRef.current = template?.id;
      hasUserEditedPromptRef.current = false;
      hasUserEditedNameRef.current = false;
    }

    if (isCreateMode || !template || template.id === "new-template") {
      setTemplateName("");
      setPrompt("");
      setOriginalValues({ name: "", prompt: "" });
      setIsEditing(true);
    } else {
      const initialName =
        template.name ||
        matchedTemplate?.name ||
        "";
      const initialPrompt =
        template.prompt ||
        matchedTemplate?.prompt ||
        "";
      setTemplateName(initialName);
      setPrompt(initialPrompt);
      setOriginalValues({ name: initialName, prompt: initialPrompt });
      setIsEditing(true);
    }
  }, [template?.id, isCreateMode]);

  useEffect(() => {
    if (isCreateMode || !template || template.id === "new-template") return;

    const matchedPrompt =
      matchedTemplate?.prompt ||
      "";
    const matchedName = matchedTemplate?.name || "";

    if (
      !hasUserEditedPromptRef.current &&
      matchedPrompt &&
      (!prompt || prompt === originalValues.prompt)
    ) {
      setPrompt(matchedPrompt);
      setOriginalValues((prev) => ({ ...prev, prompt: matchedPrompt }));
    }

    if (
      !hasUserEditedNameRef.current &&
      matchedName &&
      (!templateName || templateName === originalValues.name)
    ) {
      setTemplateName(matchedName);
      setOriginalValues((prev) => ({ ...prev, name: matchedName }));
    }
  }, [matchedTemplate, isCreateMode, template]);

  const isNameChanged = templateName.trim() !== originalValues.name.trim();
  const isPromptChanged = prompt.trim() !== originalValues.prompt.trim();
  const hasUnsavedChanges = isNameChanged || isPromptChanged;

  useEffect(() => {
    if (!isEditing || !hasUnsavedChanges || showDiscardConfirm) return;

    const handleOutsideClick = (event: MouseEvent) => {
      const element = event.target instanceof Element ? event.target : null;
      if (!element) return;
      const target =
        (element.closest("button, [role='tab'], [role='button'], a") as HTMLElement | null) ||
        element.parentElement;
      if (!target) return;
      if (target.closest(".template-edit-container")) return;
      if (target.closest(".MuiDialog-root")) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      pendingOutsideClickRef.current = target;
      setShowDiscardConfirm(true);
    };

    document.addEventListener("click", handleOutsideClick, true);
    return () => document.removeEventListener("click", handleOutsideClick, true);
  }, [isEditing, hasUnsavedChanges, showDiscardConfirm]);

  const continuePendingOutsideClick = () => {
    const target = pendingOutsideClickRef.current;
    pendingOutsideClickRef.current = null;
    if (target?.isConnected) window.setTimeout(() => target.click(), 0);
  };

  const handleSave = async (): Promise<boolean> => {
    try {
      const isNew =
        isCreateMode ||
        !template?.id ||
        template.id === "new-template" ||
        template.id.startsWith("custom-template-");

      const trimmedName = templateName.trim();
      const trimmedPrompt = prompt.trim();
      const payload: PostTemplateCreateOrUpdateParams = {};

      if (isNew) {
        if (!trimmedName) {
          showSnackbar("error", "Please provide a template name");
          return false;
        }
        if (!trimmedPrompt) {
          showSnackbar("error", "Please provide an extraction prompt");
          return false;
        }
        payload.templateName = trimmedName;
        payload.templatePrompt = trimmedPrompt;
      } else {
        const originalName = (
          originalValues.name ||
          template?.name ||
          ""
        ).trim();
        const originalPrompt = (
          originalValues.prompt ||
          template?.prompt ||
          ""
        ).trim();
        const nameChanged = trimmedName !== originalName;
        const promptChanged = trimmedPrompt !== originalPrompt;

        if (nameChanged && !trimmedName) {
          showSnackbar("error", "Template name cannot be empty");
          return false;
        }

        if (promptChanged && !trimmedPrompt) {
          showSnackbar("error", "Extraction prompt cannot be empty");
          return false;
        }

        if (!nameChanged && !promptChanged) {
          setIsEditing(false);
          setShowDiscardConfirm(false);
          return true;
        }

        const effectiveTemplateId =
          template?.templateId ||
          matchedTemplate?.templateId ||
          matchedTemplate?.id ||
          template?.id;

        if (effectiveTemplateId && effectiveTemplateId !== "new-template") {
          payload.templateId = effectiveTemplateId;
        }

        if (nameChanged) {
          payload.templateName = trimmedName;
        }

        if (promptChanged) {
          payload.templatePrompt = trimmedPrompt;
        }
      }

      const res = await postTemplateCreateOrUpdate(payload);
      const savedId =
        getTemplateIdFromResponse(res) ||
        template?.templateId ||
        matchedTemplate?.templateId ||
        template?.id;

      const savedItem: TemplateItem = {
        // In create mode the tab still has the sentinel id. Prefer the id
        // returned by the API so the tab transitions to update mode after save.
        id: savedId || template?.id || "new-template",
        name:
          trimmedName ||
          template?.name ||
          (isCreateMode ? "New template" : "Untitled Template"),
        prompt: trimmedPrompt,
        scope: template?.scope || matchedTemplate?.scope,
        templateId: savedId,
        assetId: template?.assetId,
      };

      justSavedRef.current = true;
      hasUserEditedNameRef.current = false;
      hasUserEditedPromptRef.current = false;
      setOriginalValues({ name: trimmedName, prompt: trimmedPrompt });
      setIsEditing(false);
      setShowDiscardConfirm(false);

      await refreshTemplateList();
      showSnackbar("success", `${savedItem.name} saved successfully`);
      onSave?.(savedItem);
      onSaveSuccess?.(savedItem);
      return true;
    } catch (err: any) {
      showSnackbar(
        "error",
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save template",
      );
      return false;
    }
  };

  const handleCancelClick = () => {
    if (hasUnsavedChanges) {
      setShowDiscardConfirm(true);
    } else {
      handleDiscard();
    }
  };

  const handleDiscard = () => {
    setShowDiscardConfirm(false);
    hasUserEditedNameRef.current = false;
    hasUserEditedPromptRef.current = false;
    setTemplateName(originalValues.name);
    setPrompt(originalValues.prompt);
    setIsEditing(false);
    onCancel?.();
  };

  const handleRun = async () => {
    if (!isCreateMode && !isRegenerateMode && isEditing) {
      showSnackbar(
        "info",
        "Please save your changes before running extraction",
      );
      return;
    }

    try {
      const isNew =
        isCreateMode ||
        !template?.id ||
        template.id === "new-template" ||
        template.id.startsWith("custom-template-");

      let resolvedId = template?.id || "new-template";
      const trimmedName = templateName.trim();
      const trimmedPrompt = prompt.trim();

      const effectiveTemplateId =
        template?.templateId ||
        matchedTemplate?.templateId ||
        matchedTemplate?.id ||
        template?.id;

      if (isNew) {
        if (!trimmedName) {
          showSnackbar("error", "Please provide a template name");
          return;
        }
        if (!trimmedPrompt) {
          showSnackbar("error", "Please provide an extraction prompt");
          return;
        }
        const payload: PostTemplateCreateOrUpdateParams = {
          templateName: trimmedName,
          templatePrompt: trimmedPrompt,
        };
        const res = await postTemplateCreateOrUpdate(payload);
        resolvedId = getTemplateIdFromResponse(res) || resolvedId;
        await refreshTemplateList();
      } else {
        const originalName = (
          originalValues.name ||
          template?.name ||
          ""
        ).trim();
        const originalPrompt = (
          originalValues.prompt ||
          template?.prompt ||
          ""
        ).trim();
        const nameChanged = trimmedName !== originalName;
        const promptChanged = trimmedPrompt !== originalPrompt;

        if (nameChanged && !trimmedName) {
          showSnackbar("error", "Template name cannot be empty");
          return;
        }

        if (promptChanged && !trimmedPrompt) {
          showSnackbar("error", "Extraction prompt cannot be empty");
          return;
        }

        if (nameChanged || promptChanged) {
          const payload: PostTemplateCreateOrUpdateParams = {};
          if (effectiveTemplateId && effectiveTemplateId !== "new-template") {
            payload.templateId = effectiveTemplateId;
          }
          if (nameChanged) payload.templateName = trimmedName;
          if (promptChanged) payload.templatePrompt = trimmedPrompt;

          await postTemplateCreateOrUpdate(payload);
          await refreshTemplateList();
        }
      }

      let finalAssetId =
        matchingCollateral?.interactionAssetId ||
        template?.assetId;

      let regenerationStarted = false;
      if (isRegenerateMode && interactionId) {
        const genRes = await generateTemplateExtractionV2Mutation({
          interactionId,
          transcriptAssetId: transcriptAssetId || "",
          templateId: effectiveTemplateId || resolvedId,
          assetId: finalAssetId,
          regenerate: true,
        });
        regenerationStarted = true;

        const newAssetId =
          genRes?.assetId ||
          genRes?.data?.assetId ||
          finalAssetId;

        if (newAssetId) {
          finalAssetId = newAssetId;
        }

        queryClient.invalidateQueries({
          queryKey: ["get_template_extraction_v2"],
        });
        queryClient.invalidateQueries({
          queryKey: ["get_interaction_collateral"],
        });
        showSnackbar(
          "success",
          `${trimmedName || template?.name || "Template"} regeneration initiated`,
        );
      }

      const targetTemplate: TemplateItem = {
        id: isNew
          ? resolvedId || template?.id || "new-template"
          : template?.id || resolvedId,
        name:
          trimmedName ||
          template?.name ||
          (isCreateMode ? "New template" : "Untitled Template"),
        prompt: trimmedPrompt,
        scope: template?.scope || matchedTemplate?.scope,
        assetId: finalAssetId,
        templateId: isNew ? resolvedId : effectiveTemplateId || resolvedId,
        shouldRegenerate: false,
        isGenerating: isRegenerateMode,
      };

      onRun?.(targetTemplate, {
        regenerate: false,
        alreadyStarted: regenerationStarted,
      });
    } catch (err: any) {
      showSnackbar(
        "error",
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save and run template",
      );
    }
  };

  const templateNameActions = !isCreateMode && !isRegenerateMode && !isEditing ? (
    <Box className="template-settings-template-actions">
      <Button
        size="small"
        variant="outlined"
        startIcon={<EditOutlinedIcon />}
        onClick={() => setIsEditing(true)}
      >
        Edit
      </Button>
    </Box>
  ) : null;

  const templateEditorActions = (
    <Box className="template-edit-bottom-actions">
      {isRegenerateMode ? (
        <>
          <Button
            onClick={handleCancelClick}
            className="template-edit-cancel-btn"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRun}
            className="template-edit-run-btn"
            variant="contained"
            disabled={effectiveSaving || !hasUnsavedChanges}
          >
            {effectiveSaving && <CircularProgress size={14} sx={{ mr: 1, color: "inherit" }} />}
            Save & Run template extraction
          </Button>
        </>
      ) : !isCreateMode ? (
        isEditing ? (
          <Box className="template-edit-prompt-actions">
            <Button
              onClick={() => handleSave()}
              className="template-edit-save-btn"
              variant="contained"
            >
              {effectiveSaving && <CircularProgress size={14} sx={{ mr: 1, color: "inherit" }} />}
              {effectiveSaving ? "Saving..." : "Save changes"}
            </Button>
            <Button
              onClick={handleCancelClick}
              className="template-edit-cancel-btn"
              variant="outlined"
              disabled={effectiveSaving}
            >
              Cancel
            </Button>
          </Box>
        ) : (
          <Button
            onClick={handleCancelClick}
            className="template-edit-cancel-btn"
            variant="outlined"
          >
            Cancel
          </Button>
        )
      ) : (
        <Button
          onClick={handleCancelClick}
          className="template-edit-cancel-btn"
          variant="outlined"
        >
          Cancel
        </Button>
      )}

      {!isRegenerateMode && (
        <Button
          onClick={handleRun}
          className="template-edit-run-btn"
          variant="contained"
          disabled={effectiveSaving || (!isCreateMode && isEditing)}
        >
          {isCreateMode ? "Save & Run template extraction" : "Run template extraction"}
        </Button>
      )}
    </Box>
  );

  return (
    <Box className="template-edit-container">
      {/* Page Header */}
      <Box className="template-edit-header">
        <Box className="template-edit-icon-box">
          <img
            src={TemplateBasedColorIcon}
            alt="template icon"
            className="template-edit-icon"
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography className="template-edit-title">
              {isRegenerateMode
                ? `Regenerate ${template?.displayName || template?.name || "Template"}`
                : "Template-based extraction"}
            </Typography>
          </Box>
          <Typography className="template-edit-subtitle">
            {isRegenerateMode
              ? "Once regenerated, it will overwrite the existing data!"
              : "Create a structured result from a preset or your own instructions."}
          </Typography>
        </Box>
      </Box>

      {/* Form Card */}
      <Box className="template-edit-card">
        <EditTemplates
          templateName={templateName}
          prompt={prompt}
          onTemplateNameChange={(value) => {
            hasUserEditedNameRef.current = true;
            setTemplateName(value);
          }}
          onPromptChange={(value) => {
            hasUserEditedPromptRef.current = true;
            setPrompt(value);
          }}
          readOnly={!isCreateMode && !isRegenerateMode && !isEditing}
          disabled={
            effectiveSaving ||
            (!isCreateMode && !isRegenerateMode && !isEditing)
          }
          showGuidance={isCreateMode}
          nameActions={templateNameActions}
          footer={templateEditorActions}
        />
      </Box>

      {/* Unsaved Changes Confirmation Dialog */}
      <CustomConfirmation
        isOpen={showDiscardConfirm}
        title="Unsaved Changes"
        message="You have unsaved changes. Do you want to discard or save them?"
        width="380px"
        actionBtnLabel="Save"
        cancelBtnLabel="Discard"
        handleSuccess={() => {
          void handleSave().then((saved) => {
            if (saved) continuePendingOutsideClick();
          });
        }}
        handleClose={() => {
          handleDiscard();
          continuePendingOutsideClick();
        }}
        handleCancel={() => {
          // CANCEL - just close the confirmation and keep the dialog open
          setShowDiscardConfirm(false);
        }}
        backgroundcolor
        actionType="save"
      />
    </Box>
  );
};

export const editTemplateBasedExtraction = EditTemplateBasedExtraction;
export default EditTemplateBasedExtraction;
